import type {
  OrganizationGivingAccount,
  OrganizationGivingAccountResponse,
  OrganizationImageSignResponse,
  OrganizationImageSignResult,
  Organization,
  OrganizationDailySeries,
  OrganizationDailySeriesResponse,
  OrganizationFund,
  OrganizationFundResponse,
  OrganizationResponse,
  OrganizationServiceTime,
  OrganizationServiceTimeResponse,
  UserOrganization,
  UserOrganizationResponse,
  OrganizationMember,
  OrganizationMemberResponse,
  AddMemberResult,
  AddMemberResponse,
  UpdateMemberResult,
  UpdateMemberResponse,
  MergeOrganizationResult,
  MergeOrganizationResultResponse,
} from '../types';

export function mapOrganization(data: OrganizationResponse): Organization {
  return {
    id: data.id,
    name: data.name,
    handle: data.handle,
    image: data.image,
    type: data.type,
    city: data.city,
    state: data.state,
    country: data.country,
    streetAddress: data.street_address,
    phone: data.phone,
    website: data.website,
    religion: data.religion,
    denomination: data.denomination,
    description: data.description ?? null,
    zipcode: data.zipcode ?? null,
    latitude: data.latitude ?? null,
    longitude: data.longitude ?? null,
    searchRank: data.search_rank ?? null,
    approxSize: data.approx_size ?? null,
    launchedAt: data.launched_at ?? null,
    typeId: data.type_id ?? null,
    ownerUserId: data.owner_user_id ?? null,
    mergedInto: data.merged_into ?? null,
    isGivingEnabled: data.is_giving_enabled ?? null,
    destroyed: data.destroyed,
    memberCount: data.member_count,
    leaderCount: data.leader_count,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function mapUserOrganization(data: UserOrganizationResponse): UserOrganization {
  return {
    organization: {
      id: data.organization.id,
      name: data.organization.name,
      handle: data.organization.handle,
      image: data.organization.image,
      type: data.organization.type,
      destroyed: data.organization.destroyed,
    },
    membership: {
      role: data.membership.role,
      title: data.membership.title,
      destroyed: data.membership.destroyed,
      createdAt: data.membership.created_at,
      updatedAt: data.membership.updated_at,
    },
  };
}

export function mapOrganizationMember(data: OrganizationMemberResponse): OrganizationMember {
  return {
    user: {
      id: data.user.id,
      name: data.user.name,
      firstName: data.user.first_name,
      lastName: data.user.last_name,
      email: data.user.email,
      phone: data.user.phone,
      countryCode: data.user.country_code,
      profileImage: data.user.profile_image,
      createdAt: data.user.created_at,
    },
    membership: {
      role: data.membership.role,
      title: data.membership.title,
      hasPermissionAccessAnalytics: data.membership.has_permission_access_analytics,
      createdAt: data.membership.created_at,
      updatedAt: data.membership.updated_at,
    },
  };
}

export function mapAddMemberResult(data: AddMemberResponse): AddMemberResult {
  return {
    userId: data.user.id,
    userName: data.user.name,
    role: data.membership.role,
    title: data.membership.title,
  };
}

export function mapUpdateMemberResult(data: UpdateMemberResponse): UpdateMemberResult {
  return {
    userId: data.user.id,
    userName: data.user.name,
    role: data.membership.role,
    title: data.membership.title,
  };
}

export function mapOrganizationServiceTime(data: OrganizationServiceTimeResponse): OrganizationServiceTime {
  return {
    id: data.id,
    name: data.name,
    day: data.day,
    time: data.time,
    address: data.address,
    latitude: data.latitude,
    longitude: data.longitude,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function mapOrganizationFund(data: OrganizationFundResponse): OrganizationFund {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    isDefault: data.default,
    isEnabled: data.is_enabled,
    isPrimaryDonation: data.is_primary_donation,
    includeInAppPromotions: data.include_in_app_promotions,
    imageUrl: data.image_url,
    defaultAmountMonthly: data.default_amount_monthly,
    defaultAmountYearly: data.default_amount_yearly,
    organizationId: data.organization_id,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  };
}

export function mapOrganizationDailySeries(data: OrganizationDailySeriesResponse): OrganizationDailySeries {
  return {
    organizationId: data.organization_id,
    dailySeriesId: data.daily_series_id,
    disabled: data.disabled,
    type: data.type,
  };
}

export function mapMergeOrganizationResult(data: MergeOrganizationResultResponse): MergeOrganizationResult {
  return {
    organizationId: data.organization_id,
    success: data.success,
    error: data.error ?? null,
  };
}

export function mapOrganizationImageSignResult(data: OrganizationImageSignResponse): OrganizationImageSignResult {
  return {
    url: data.url,
    signedRequest: data.signed_request,
  };
}

export function mapOrganizationGivingAccount(data: OrganizationGivingAccountResponse): OrganizationGivingAccount {
  return {
    id: data.id,
    givingAccountId: data.giving_account_id,
    status: data.status,
    paymentsEnabled: data.payments_enabled,
    payoutsEnabled: data.payouts_enabled,
    isGivingEnabled: data.is_giving_enabled,
    manuallyDisableGiving: data.manually_disable_giving,
    manualReviewStatus: data.manual_review_status,
    reviewerName: data.reviewer_name,
    reviewerNote: data.reviewer_note,
    extraGivingEmail: data.extra_giving_email,
    country: data.country,
    defaultCurrency: data.default_currency,
    payoutInterval: data.payout_interval,
    createdAt: data.created_at,
    leader: data.leader ? { id: data.leader.id, name: data.leader.name } : null,
    accountLinkAvailable: data.account_link_available,
  };
}
